import { useRef } from "react";
import html2canvas from "html2canvas";
import { Button } from "@/components/ui/button";
import { Download, MessageSquare, ChevronRight } from "lucide-react";

export type SharePathKey = "job" | "earn-learn" | "govt" | "skills";

const PATH_CONTENT: Record<
  SharePathKey,
  { title: string; subtitle: string; steps: [string, string, string]; whatsapp: string }
> = {
  job: {
    title: "Job Fast Track",
    subtitle: "Training → certification → placement in 3–6 months",
    steps: ["PMKVY training", "Certification", "Placement"],
    whatsapp:
      "I just took the Career Quiz on we-help.in and got Job Fast Track! Find free PMKVY training, certification & placement help. Try the 2-min quiz: https://we-help.in",
  },
  "earn-learn": {
    title: "Earn While Learn",
    subtitle: "₹5,000–₹9,000/month stipend while you train",
    steps: ["NAPS registration", "Apprenticeship", "Full-time role"],
    whatsapp:
      "My we-help.in career quiz result: Earn While Learn! Get ₹5,000–9,000/month stipend through apprenticeships. Take the free 2-min quiz: https://we-help.in",
  },
  govt: {
    title: "Government Career",
    subtitle: "Free Skill India + SWAYAM courses for govt job prep",
    steps: ["Skill India + SWAYAM", "Certifications", "Govt job prep"],
    whatsapp:
      "Took the Career Quiz on we-help.in — my path is Government Career! Free Skill India & SWAYAM courses to prep for govt jobs. Try it: https://we-help.in",
  },
  skills: {
    title: "Skill & Freelance",
    subtitle: "Build skills, portfolio and go independent",
    steps: ["Online courses", "Portfolio", "Freelance / startup"],
    whatsapp:
      "My we-help.in quiz result: Skill & Freelance! Build skills, create a portfolio, and work independently. Free 2-min quiz: https://we-help.in",
  },
};

interface Props {
  pathKey: SharePathKey;
  onRetake: () => void;
}

export const ShareableResultCard = ({ pathKey, onRetake }: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const content = PATH_CONTENT[pathKey];

  const handleDownload = async () => {
    if (!cardRef.current) return;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    const link = document.createElement("a");
    link.download = "my-career-path-we-help.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(content.whatsapp)}`;

  return (
    <div className="flex flex-col items-center gap-5 py-4">
      {/* Card */}
      <div
        id="result-card"
        ref={cardRef}
        className="w-full max-w-[400px] rounded-2xl overflow-hidden shadow-card border border-border bg-white"
        style={{ borderRadius: "16px" }}
      >
        {/* Top section — teal-tinted */}
        <div style={{ backgroundColor: "#E1F5EE" }} className="p-6">
          <div className="flex items-baseline gap-2 mb-5">
            <span className="text-xl font-extrabold tracking-tight" style={{ color: "#1D9E75" }}>
              we-help.in
            </span>
            <span className="text-[11px] text-muted-foreground font-medium">
              A SkillSTEM initiative
            </span>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-2">
            My career path
          </p>
          <h3
            className="text-3xl font-extrabold leading-tight mb-2"
            style={{ color: "#0f3a2c" }}
          >
            {content.title}
          </h3>
          <p className="text-sm text-foreground/80 leading-snug">{content.subtitle}</p>
        </div>

        {/* Bottom section — white */}
        <div className="bg-white p-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground mb-3">
            My roadmap
          </p>
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2 mb-5">
            {content.steps.map((step, i) => (
              <span key={i} className="inline-flex items-center gap-1.5">
                <span className="text-sm font-semibold text-foreground">{step}</span>
                {i < content.steps.length - 1 && (
                  <ChevronRight className="h-4 w-4" style={{ color: "#1D9E75" }} />
                )}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
            <p className="text-xs font-medium text-foreground">
              Find your path free at{" "}
              <span className="font-bold" style={{ color: "#1D9E75" }}>
                we-help.in
              </span>
            </p>
            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap"
              style={{ backgroundColor: "#1D9E75", color: "#ffffff" }}
            >
              2-min quiz
            </span>
          </div>
        </div>
      </div>

      {/* Share buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-[400px]">
        <Button asChild variant="whatsapp" size="lg" className="flex-1">
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
            <MessageSquare className="h-4 w-4" />
            Share on WhatsApp
          </a>
        </Button>
        <Button onClick={handleDownload} variant="outline" size="lg" className="flex-1">
          <Download className="h-4 w-4" />
          Download card
        </Button>
      </div>

      <button
        onClick={onRetake}
        className="text-sm font-medium text-primary hover:underline underline-offset-4"
      >
        Retake quiz
      </button>
    </div>
  );
};